'use client';

import { useEffect, useRef, type CSSProperties, type RefObject } from 'react';
import { T } from '@/lib/tokens';

export type OrbState = 'idle' | 'speaking' | 'listening' | 'thinking';

/** Mutable levels written by the audio pollers — Orb reads these without React state. */
export type OrbLiveLevels = { mic: number; ai: number };

function applyOrbLevelVars(
  el: HTMLElement,
  speakLevel: number,
  listenLevel: number,
) {
  el.style.setProperty('--orb-speak', speakLevel.toFixed(4));
  el.style.setProperty('--orb-listen', listenLevel.toFixed(4));
  // Match prior ringsActive floor (mic > 0.06): glow is 0 when quiet so rings stay dark.
  const listenGlow = listenLevel > 0.06 ? 0.35 + listenLevel * 0.45 : 0;
  el.style.setProperty('--orb-listen-glow', listenGlow.toFixed(4));
}

/** Fake audio motion for demos when no live levels are supplied. Writes CSS vars only. */
function useFakeLevelsOnElement(
  elRef: RefObject<HTMLElement | null>,
  speakActive: boolean,
  listenActive: boolean,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let t = 0;
    const tick = () => {
      const el = elRef.current;
      t += 0.05;
      const wave =
        0.45 +
        Math.sin(t * 1.7) * 0.18 +
        Math.sin(t * 4.3 + 1.2) * 0.12 +
        (Math.random() - 0.5) * 0.08;
      const level = Math.max(0.05, Math.min(1, wave));
      if (el) {
        applyOrbLevelVars(
          el,
          speakActive ? level : 0,
          listenActive ? level : 0,
        );
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [elRef, speakActive, listenActive, enabled]);
}

interface OrbProps {
  state?: OrbState;
  size?: number;
  /**
   * Live mic/AI levels from the realtime session. Updated every animation frame
   * by mutating the ref — does not trigger React re-renders.
   */
  liveLevelsRef?: RefObject<OrbLiveLevels>;
  /**
   * Static mic level (0-1). Prefer `liveLevelsRef` for realtime audio.
   * When omitted and not using liveLevelsRef in listening state, a fake signal is used.
   */
  micLevel?: number;
  /**
   * Static AI level (0-1). Prefer `liveLevelsRef` for realtime audio.
   * When omitted and not using liveLevelsRef in speaking state, a fake signal is used.
   */
  aiLevel?: number;
  /** When false, listen rings stay dark even if levels update (e.g. paused). */
  micActive?: boolean;
  /** When false, speak halo ignores AI level. Defaults to state === 'speaking'. */
  aiActive?: boolean;
}

export function Orb({
  state = 'idle',
  size = 240,
  liveLevelsRef,
  micLevel,
  aiLevel,
  micActive = true,
  aiActive,
}: OrbProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const speaking = state === 'speaking';
  const listening = state === 'listening';
  const thinking = state === 'thinking';
  const speakOn = aiActive ?? speaking;
  // Rings follow real mic whenever live/static mic is available — not only in
  // 'listening' — so speech during post-AI settle still lights the rings.
  const hasLive = liveLevelsRef !== undefined;
  const hasStaticMic = micLevel !== undefined;
  const hasStaticAi = aiLevel !== undefined;
  const useFake =
    !hasLive &&
    ((speakOn && !hasStaticAi) || (listening && !hasStaticMic && micActive));

  useFakeLevelsOnElement(
    rootRef,
    speakOn && !hasStaticAi && !hasLive,
    listening && !hasStaticMic && !hasLive && micActive,
    useFake,
  );

  // Live path: copy mutable ref → CSS vars every frame (no React setState).
  useEffect(() => {
    if (!liveLevelsRef) return;
    let raf = 0;
    const tick = () => {
      const el = rootRef.current;
      if (el) {
        const { mic, ai } = liveLevelsRef.current;
        const speakLevel = speakOn ? ai : 0;
        // Drive rings from real mic whenever supplied + micActive.
        const listenLevel = micActive ? mic : 0;
        applyOrbLevelVars(el, speakLevel, listenLevel);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [liveLevelsRef, speakOn, micActive]);

  // Static prop path: sync occasional prop changes into CSS vars.
  useEffect(() => {
    if (hasLive || useFake) return;
    const el = rootRef.current;
    if (!el) return;
    const speakLevel = speakOn ? (aiLevel ?? 0) : 0;
    const listen = micActive && hasStaticMic ? (micLevel ?? 0) : 0;
    applyOrbLevelVars(el, speakLevel, listen);
  }, [
    hasLive,
    useFake,
    speakOn,
    micActive,
    hasStaticMic,
    micLevel,
    aiLevel,
  ]);

  // Reset vars when gates turn off.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (!speakOn) el.style.setProperty('--orb-speak', '0');
    if (!micActive) {
      el.style.setProperty('--orb-listen', '0');
      el.style.setProperty('--orb-listen-glow', '0');
    }
  }, [speakOn, micActive]);

  const rootStyle = {
    width: size,
    height: size,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Defaults before first rAF tick
    '--orb-speak': 0,
    '--orb-listen': 0,
    '--orb-listen-glow': 0,
    '--orb-size': `${size}px`,
    '--orb-speak-on': speakOn ? 1 : 0,
    '--orb-listen-on': micActive ? 1 : 0,
  } as CSSProperties;

  return (
    <div ref={rootRef} style={rootStyle} data-orb-state={state}>
      {/* Terra/orange halo — AI speaking. Intensity from --orb-speak. */}
      <div
        className="orb-halo-outer"
        style={{
          position: 'absolute',
          inset: speakOn
            ? 'calc(var(--orb-size) * -0.2 - var(--orb-size) * var(--orb-speak) * 0.22)'
            : 'calc(var(--orb-size) * -0.25)',
          background:
            'radial-gradient(circle, oklch(78% 0.13 48 / calc((0.14 + var(--orb-speak-on) * (0.41 + var(--orb-speak) * 0.4)) * 0.5)) 0%, transparent 65%)',
          filter: 'blur(18px)',
          transition: speakOn ? 'inset 0.08s' : 'background 0.35s, inset 0.35s',
          pointerEvents: 'none',
        }}
      />
      <div
        className="orb-halo-mid"
        style={{
          position: 'absolute',
          inset: 'calc(var(--orb-size) * -0.1)',
          background:
            'radial-gradient(circle, oklch(72% 0.14 48 / calc(0.14 + var(--orb-speak-on) * (0.41 + var(--orb-speak) * 0.4))) 0%, transparent 60%)',
          filter: 'blur(8px)',
          transition: speakOn ? undefined : 'background 0.2s',
          pointerEvents: 'none',
        }}
      />

      {/* Equatorial rings — participant voice via --orb-listen */}
      {[
        { s: 0.8, threshold: 0.0, maxOp: 0.88, baseW: 1.5, maxW: 4.5 },
        { s: 0.94, threshold: 0.28, maxOp: 0.58, baseW: 1.2, maxW: 3.0 },
        { s: 1.08, threshold: 0.52, maxOp: 0.38, baseW: 0.9, maxW: 2.0 },
        { s: 1.22, threshold: 0.65, maxOp: 0.22, baseW: 0.7, maxW: 1.5 },
      ].map(({ s, threshold, maxOp, baseW, maxW }, i) => (
        <div
          key={i}
          className="orb-ring"
          style={{
            position: 'absolute',
            width: `calc(var(--orb-size) * ${s} + var(--orb-size) * var(--orb-listen) * var(--orb-listen-on) * 0.08 + (var(--orb-listen) * var(--orb-listen-on) * ${maxW - baseW}px))`,
            height: `calc(var(--orb-size) * ${s} + var(--orb-size) * var(--orb-listen) * var(--orb-listen-on) * 0.08 + (var(--orb-listen) * var(--orb-listen-on) * ${maxW - baseW}px))`,
            borderRadius: '50%',
            border: `calc(${baseW}px + var(--orb-listen) * var(--orb-listen-on) * ${maxW - baseW}px) solid oklch(70% 0.11 55 / 0.88)`,
            boxShadow: `0 0 ${5 + i * 3.5}px oklch(72% 0.10 55 / 0.33)`,
            filter: `blur(${(1.6 + i * 0.9).toFixed(2)}px)`,
            opacity: `calc(var(--orb-listen-on) * min(1, max(0, (var(--orb-listen-glow) - ${threshold}) * 2.4)) * ${maxOp})`,
            transition: 'opacity 0.18s ease-out',
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Orb body */}
      <div
        style={{
          width: size * 0.7,
          height: size * 0.7,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 35% 30%, oklch(82% 0.10 55), oklch(62% 0.13 48) 55%, oklch(48% 0.14 45) 100%)',
          boxShadow: `
            inset -8px -16px 32px oklch(40% 0.13 45 / 0.6),
            inset 6px 8px 24px oklch(94% 0.04 65 / 0.5),
            0 12px 40px oklch(48% 0.14 45 / 0.35)
          `,
          position: 'relative',
          animation: thinking
            ? 'orbBreathe 1.4s ease-in-out infinite'
            : 'orbBreathe 4s ease-in-out infinite',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '12%',
            left: '20%',
            width: '38%',
            height: '32%',
            borderRadius: '50%',
            background:
              'radial-gradient(ellipse, oklch(98% 0.02 70 / 0.7), transparent 70%)',
            filter: 'blur(4px)',
          }}
        />
      </div>

      {thinking && (
        <div style={{ position: 'absolute', bottom: -40, display: 'flex', gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: T.terra,
                animation: `orbDot 1.2s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
